import { SurveyQuestion } from "../models";
import { BranchingRules } from "../shared/Types";
import getQuestionContent from "./getQuestionContent";

const getBranchingRule = (
  question: SurveyQuestion
): BranchingRules | undefined => {
  if (question.bool?.branchingRule) {
    return { ...question.bool.branchingRule };
  }
  if (question.choice?.branchingRule) {
    return { ...question.choice.branchingRule };
  }
  if (question.info) {
    return undefined;
  }
  if (question.scale?.branchingRule) {
    return { ...question.scale.branchingRule };
  }
  if (question.textEntry) {
    return undefined;
  }
  if (question.timeOfDay?.branchingRule) {
    return { ...question.timeOfDay.branchingRule };
  }
  return undefined;
};

export const clearBranchingRule = (
  question: SurveyQuestion
): SurveyQuestion => {
  if (question.bool?.branchingRule) {
    return {
      ...question,
      bool: {
        ...question.bool,
        branchingRule: {
          ...question.bool.branchingRule,
          destinationQuestionId: "",
          defaultQuestionId: "",
        },
      },
    };
  }
  if (question.choice?.branchingRule) {
    return {
      ...question,
      choice: {
        ...question.choice,
        branchingRule: {
          ...question.choice.branchingRule,
          destinationQuestionId: "",
          defaultQuestionId: "",
          ...(question.choice.branchingRule.destinations !== undefined &&
          question.choice.branchingRule.destinations !== null
            ? { destinations: [] }
            : {}),
        },
      },
    };
  }
  if (question.scale?.branchingRule) {
    return {
      ...question,
      scale: {
        ...question.scale,
        branchingRule: {
          ...question.scale.branchingRule,
          destinationQuestionId: "",
          defaultQuestionId: "",
        },
      },
    };
  }
  if (question.timeOfDay?.branchingRule) {
    return {
      ...question,
      timeOfDay: {
        ...question.timeOfDay,
        branchingRule: {
          ...question.timeOfDay.branchingRule,
          destinationQuestionId: "",
          defaultQuestionId: "",
        },
      },
    };
  }
  return { ...question };
};

const hasPerChoiceDestinations = (question: SurveyQuestion | null) =>
  question?.choice?.branchingRule?.destinations !== undefined &&
  question?.choice?.branchingRule?.destinations !== null;

const hasDuplicateIDs = (ids: string[]) =>
  ids.some((id, index) => ids.indexOf(id) !== index);

const hasInvalidPerChoiceDestinations = (question: SurveyQuestion | null) => {
  if (!hasPerChoiceDestinations(question)) return false;

  const choice = question!.choice!;
  const choiceIDs = choice.textChoices.map(item => item?.textChoiceID);
  const validChoiceIDs = choiceIDs.filter(
    (choiceID): choiceID is string => !!choiceID
  );
  const destinations = choice.branchingRule!.destinations!;
  const destinationChoiceIDs = destinations
    .filter((destination): destination is NonNullable<typeof destination> => destination !== null)
    .map(destination => destination.choiceId);

  // Per-choice branching is only valid for single-select questions. Selected
  // choices have individual destinations; all unselected choices use defaultQuestionId.
  return (
    choice.multipleChoice !== false ||
    choiceIDs.length === 0 ||
    validChoiceIDs.length !== choiceIDs.length ||
    hasDuplicateIDs(validChoiceIDs) ||
    hasDuplicateIDs(destinationChoiceIDs) ||
    destinations.some(destination =>
      !destination ||
      !destination.choiceId ||
      !validChoiceIDs.includes(destination.choiceId) ||
      !destination.destinationQuestionId
    )
  );
};

export const questionsWithBlankBranchDestinations = (
  questions: (SurveyQuestion | null)[]
) => {
  const questionIDs: String[] = [];
  questions.forEach((question) => {
    const branchingRule = getBranchingRule(question as SurveyQuestion);
    if (!branchingRule) return;

    const questionID = getQuestionContent(question)?.questionId;
    const usesPerChoiceDestinations = hasPerChoiceDestinations(question);
    const hasBlankDefaultDestination = !branchingRule?.defaultQuestionId;
    const hasBlankLegacyDestination =
      !usesPerChoiceDestinations && !branchingRule?.destinationQuestionId;
    const hasInvalidPerChoiceRule = hasInvalidPerChoiceDestinations(question);
    const hasInvalidBranchingRule =
      hasBlankDefaultDestination ||
      hasBlankLegacyDestination ||
      hasInvalidPerChoiceRule;

    if (hasInvalidBranchingRule && questionID) {
      questionIDs.push(questionID);
    }
  });
  return questionIDs;
};

export default getBranchingRule;
